/*
 * Copyright 2026 Mifos Initiative
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/openMF/kmp-project-template/blob/main/LICENSE
 */
package kpt.feature.loans.di

import kpt.core.data.config.AppOutboxQualifiers
import kpt.feature.loans.LoanReminderUseCase
import kpt.feature.loans.ui.EditLoanViewModel
import kpt.feature.loans.ui.LoanDetailViewModel
import kpt.feature.loans.ui.PersonalLoansListViewModel
import org.koin.core.module.dsl.singleOf
import org.koin.core.module.dsl.viewModel
import org.koin.dsl.module

/**
 * Koin module for the personal-loans feature.
 *
 * Wired into the app graph by `cmp-navigation/.../KoinModules.kt`
 * (`featureModule.includes(LoansModule)`). The `SubmitOutbox<Loan>` and `LoanRepository`
 * bindings consumed here are provided by `core/data/.../RepositoryModule.kt`.
 *
 * **D14 fence — the loans VMs stay factory-bound (`viewModel {}`), never singleton
 * (`single<*ViewModel>`).** Nav-back-stack retention (Phase 03 `retainedKoinViewModel`)
 * scopes each VM to the enclosing `NavBackStackEntry` so re-entry gets the same
 * instance across nav pops without promoting the binding to a process-lifetime
 * singleton — the latter would leak state across navigation boundaries and defeat
 * scoped disposal. A `single<*ViewModel>` here would be refused by the fence gate.
 *
 * The [LoanReminderUseCase] binding is the cross-module proof-of-concept that
 * consumes `kpt.sync.WorkScheduler` (provided by `sync/` module's `SyncModule`)
 * to schedule due-date notifications + trigger background data sync.
 */
val LoansModule = module {
    viewModel { PersonalLoansListViewModel(repository = get()) }
    viewModel { params ->
        LoanDetailViewModel(repository = get(), loanId = params.get())
    }
    viewModel { params ->
        EditLoanViewModel(
            repository = get(),
            outbox = get(qualifier = AppOutboxQualifiers.Loan),
            loanId = params.getOrNull(),
        )
    }

    // Cross-module use case — depends on WorkScheduler from kpt.sync.di.SyncModule.
    singleOf(::LoanReminderUseCase)
}
